import React, { useLayoutEffect, useRef, useState } from 'react'
import classNames from 'classnames'
import styles from './SegmentedControl.module.scss'

export interface SegmentedControlOption {
  key: string
  label: string
  count?: number | string | null
  hideCount?: boolean
}

export type SegmentedControlSurface = 'white' | 'gray'

export type SegmentedControlSize = 'regular' | 'compact'

export interface SegmentedControlProps {
  options: SegmentedControlOption[]
  activeKey: string
  ariaLabel: string
  onSelect: (key: string) => void
  surface?: SegmentedControlSurface
  size?: SegmentedControlSize
}

interface IndicatorStyle {
  left: number
  top: number
  width: number
  height: number
}

export const SegmentedControl: React.FC<SegmentedControlProps> = ({ options, activeKey, ariaLabel, onSelect, surface = 'white', size = 'regular' }) => {
  const buttonRefs = useRef<(HTMLButtonElement | null)[]>([])
  const [indicator, setIndicator] = useState<IndicatorStyle | null>(null)

  const activeIndex = options.findIndex((option) => option.key === activeKey)
  const optionsSignature = options.map((option) => `${option.label}:${option.count}:${option.hideCount}`).join(',')
  useLayoutEffect(() => {
    const measure = () => {
      const el = activeIndex >= 0 ? buttonRefs.current[activeIndex] : null
      setIndicator(el ? { left: el.offsetLeft, top: el.offsetTop, width: el.offsetWidth, height: el.offsetHeight } : null)
    }
    measure()
    window.addEventListener('resize', measure)
    return () => window.removeEventListener('resize', measure)
  }, [activeIndex, optionsSignature])

  const handleKeyDown = (e: React.KeyboardEvent) => {
    const last = options.length - 1
    const current = activeIndex < 0 ? 0 : activeIndex
    let nextIndex: number
    switch (e.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        nextIndex = current >= last ? 0 : current + 1
        break
      case 'ArrowLeft':
      case 'ArrowUp':
        nextIndex = current <= 0 ? last : current - 1
        break
      case 'Home':
        nextIndex = 0
        break
      case 'End':
        nextIndex = last
        break
      default:
        return
    }
    e.preventDefault()
    onSelect(options[nextIndex].key)
    buttonRefs.current[nextIndex]?.focus()
  }

  return (
    <div
      role='tablist'
      aria-label={ariaLabel}
      className={classNames(
        'position-relative d-flex gap-1 align-items-center zocks-border-color-gray-200 border',
        size === 'compact' ? 'zocks-p-0.125' : 'p-1',
        { 'zocks-bg-color-gray-100': surface === 'white', 'zocks-bg-color-white': surface === 'gray' },
        styles.segment,
      )}
      onKeyDown={handleKeyDown}
    >
      {indicator ? (
        <div
          className={classNames(styles.segmentIndicator, { [styles.segmentIndicatorOnGray]: surface === 'gray' })}
          style={{ transform: `translateX(${indicator.left}px)`, top: indicator.top, width: indicator.width, height: indicator.height }}
        />
      ) : null}
      {options.map((option, index) => {
        const active = option.key === activeKey
        return (
          <button
            key={option.key}
            ref={(el) => {
              buttonRefs.current[index] = el
            }}
            type='button'
            role='tab'
            aria-selected={active}
            tabIndex={active || (activeIndex < 0 && index === 0) ? 0 : -1}
            className={classNames(
              'position-relative d-flex gap-1 align-items-center justify-content-center px-2 py-1 paragraph-small bold',
              styles.segmentButton,
              { [styles.segmentButtonCompact]: size === 'compact' },
              {
                'zocks-text-gray-900': active,
                'zocks-text-gray-600': !active,
              },
            )}
            onClick={() => onSelect(option.key)}
          >
            <span>{option.label}</span>
            {option.hideCount || option.count === undefined ? null : (
              <span className={classNames('zocks-text-gray-500', styles.segmentCount)}>{option.count === null ? '–' : option.count}</span>
            )}
          </button>
        )
      })}
    </div>
  )
}

export default SegmentedControl
